import React, { useState, useEffect } from 'react';
import styles from './LocationSourceSelector.module.css';

interface LocationSource {
  id: string;
  name: string;
}

interface LocationSourceSelectorProps {
  activeSources: LocationSource[];
  onSourcesSelected: (selectedSources: string[]) => void;
}

const LocationSourceSelector: React.FC<LocationSourceSelectorProps> = ({ 
  activeSources, 
  onSourcesSelected 
}) => {
  // Default to Cityflo if no active sources
  const defaultSources = activeSources.length === 0 
    ? [{ id: '1', name: 'Cityflo' }] 
    : activeSources;
  
  // Initialize selected sources state
  const [selectedSources, setSelectedSources] = useState<string[]>(
    defaultSources.map(source => source.id)
  );

  // Update parent component when selection changes
  useEffect(() => {
    onSourcesSelected(selectedSources);
  }, [selectedSources, onSourcesSelected]);

  // Handle checkbox changes
  const handleSourceToggle = (sourceId: string) => {
    setSelectedSources(prev => {
      if (prev.includes(sourceId)) {
        // Remove source if already selected
        return prev.filter(id => id !== sourceId);
      } else {
        // Add source if not selected
        return [...prev, sourceId];
      }
    });
  };

  // If no sources are selected, default to Cityflo
  useEffect(() => {
    if (selectedSources.length === 0) {
      setSelectedSources(['1']); // Default to Cityflo
    }
  }, [selectedSources]);

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>Select Location Sources</h3>
      
      {defaultSources.length === 0 ? (
        <p className={styles.noSources}>No active location sources available. Using Cityflo by default.</p>
      ) : (
        <div className={styles.sourcesGrid}>
          {defaultSources.map(source => (
            <div key={source.id} className={styles.sourceItem}>
              <label className={styles.sourceLabel}>
                <input
                  type="checkbox"
                  checked={selectedSources.includes(source.id)}
                  onChange={() => handleSourceToggle(source.id)}
                  className={styles.sourceCheckbox}
                />
                <span className={styles.sourceName}>{source.name}</span>
              </label>
            </div>
          ))}
        </div>
      )}
      
      <div className={styles.selectedInfo}>
        <p>
          {selectedSources.length === 0 
            ? 'No sources selected. Defaulting to Cityflo.' 
            : `Selected ${selectedSources.length} source(s) for simulation.`}
        </p>
      </div>
    </div>
  );
};

export default LocationSourceSelector;
